
import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const Docs = () => {
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Documentation</h1>
        <p className="text-muted-foreground mt-2">
          Learn how to use Agent V to manage your podcasts effectively.
        </p>
      </div>

      <Tabs defaultValue="getting-started" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="getting-started">Getting Started</TabsTrigger>
          <TabsTrigger value="guest-management">Guest Management</TabsTrigger>
          <TabsTrigger value="scheduling">Scheduling</TabsTrigger>
          <TabsTrigger value="recording">Recording</TabsTrigger>
          <TabsTrigger value="publishing">Publishing</TabsTrigger>
        </TabsList>
        
        <TabsContent value="getting-started">
          <Card>
            <CardHeader>
              <CardTitle>Getting Started with Agent V</CardTitle>
              <CardDescription>
                Learn the basics of using Agent V for podcast management
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <h3 className="text-xl font-semibold">Welcome to Agent V</h3>
              <p>
                Agent V is your all-in-one podcast management solution designed to help you focus on creating great content while automating the administrative tasks.
              </p>
              
              <h3 className="text-xl font-semibold mt-6">Key Features</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Guest discovery and management</li>
                <li>Episode scheduling and calendar integration</li>
                <li>Recording management</li>
                <li>Publishing automation</li>
                <li>Analytics and performance tracking</li>
              </ul>
              
              <h3 className="text-xl font-semibold mt-6">Initial Setup</h3>
              <p>
                To get started with Agent V, follow these steps:
              </p>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Create your podcast profile in the Settings page</li>
                <li>Connect your calendars for scheduling</li>
                <li>Set up your preferred recording equipment</li>
                <li>Configure publishing destinations</li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="guest-management">
          <Card>
            <CardHeader>
              <CardTitle>Guest Management</CardTitle>
              <CardDescription>
                Learn how to discover, contact, and manage podcast guests
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <h3 className="text-xl font-semibold">Finding Guests</h3>
              <p>
                Use the Explore page to discover potential guests based on topic, expertise, audience size, and availability.
              </p>
              
              <h3 className="text-xl font-semibold mt-6">Guest Profiles</h3>
              <p>
                Each guest has a detailed profile with contact information, social media links, previous podcast appearances, and topic expertise.
              </p>
              
              <h3 className="text-xl font-semibold mt-6">Communication</h3>
              <p>
                Agent V provides templates for outreach emails, follow-ups, and thank you messages to streamline guest communication.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="scheduling">
          <Card>
            <CardHeader>
              <CardTitle>Scheduling</CardTitle>
              <CardDescription>
                Learn how to schedule and manage podcast recording sessions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <h3 className="text-xl font-semibold">Calendar Integration</h3>
              <p>
                Agent V integrates with Google Calendar, Outlook, and other calendar services to manage your recording schedule.
              </p>
              
              <h3 className="text-xl font-semibold mt-6">Availability Settings</h3>
              <p>
                Define your availability for recordings and automatically offer these slots to guests through integrated booking links.
              </p>
              
              <h3 className="text-xl font-semibold mt-6">Reminders</h3>
              <p>
                Automated reminders are sent to both you and your guests before recording sessions to reduce no-shows.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="recording">
          <Card>
            <CardHeader>
              <CardTitle>Recording</CardTitle>
              <CardDescription>
                Learn about recording tools and best practices
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <h3 className="text-xl font-semibold">Recording Setup</h3>
              <p>
                Agent V provides recommended settings for various recording software and hardware configurations.
              </p>
              
              <h3 className="text-xl font-semibold mt-6">Remote Recording</h3>
              <p>
                Integrate with Zoom, Riverside, SquadCast, and other remote recording tools for seamless guest sessions.
              </p>
              
              <h3 className="text-xl font-semibold mt-6">Audio Quality</h3>
              <p>
                Follow our best practices guide for achieving professional audio quality with any setup.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="publishing">
          <Card>
            <CardHeader>
              <CardTitle>Publishing</CardTitle>
              <CardDescription>
                Learn how to publish and distribute your podcast episodes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <h3 className="text-xl font-semibold">Distribution Channels</h3>
              <p>
                Agent V helps you publish to major podcast platforms including Spotify, Apple Podcasts, Google Podcasts, and more.
              </p>
              
              <h3 className="text-xl font-semibold mt-6">Show Notes</h3>
              <p>
                Generate show notes automatically from your recording using our AI-powered transcription and summarization tools.
              </p>
              
              <h3 className="text-xl font-semibold mt-6">Social Media</h3>
              <p>
                Create and schedule promotional content for each episode across multiple social media platforms.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Docs;
